import { MAIN_STYLE, MAIN_STYLE_GROUPS, PADDING_SIDES, SELECTION_EVENTS } from '../shared.js'
export const msgbox = {

    parts: {
        // LV_MSGBOX_PART_BG — overall container
        main: {
            label: 'Main',
            exportSuffix: '',
            order: 0,
            propertyGroups: [...MAIN_STYLE_GROUPS],
            properties: {
                ...MAIN_STYLE,
                text_color: { type: 'color' },
                text_font:  { type: 'int', min: 8 },
                text_font_family: { type: 'string', internal: true },
                padding:    { type: 'int', min: 0, exportSpecial: true },
                ...PADDING_SIDES,
            },
        },
        // LV_MSGBOX_PART_BTN_BG (suffix 30) — button-area background
        items: {
            label: 'Button Area',
            exportSuffix: '30',
            order: 1,
            properties: {
                bg_color:     { type: 'color', format: 'hex8' },
                bg_opa:       { type: 'int',   min: 0, max: 255, internal: true },
                border_color: { type: 'color' },
                border_width: { type: 'int',   min: 0 },
                radius:       { type: 'int',   min: 0, max: 999 },
                // NOTE: `padding` here is declared `exportSpecial: true`, but ObjectExport.js's
                // padding-expansion block only special-cases part names 'main'/'knob'/
                // 'item_buttons' — there is no 'items' branch, so this value is currently
                // silently dropped on export. Confirmed pre-existing bug, deferred to Phase 3
                // (ObjectExport.js pipeline fix) per FIRMWARE-PARITY-ROADMAP.md; not touched here.
                padding:      { type: 'int',   min: 0, exportSpecial: true },
            },
        },
        // LV_MSGBOX_PART_BTN (suffix 40) — individual buttons
        item_buttons: {
            label: 'Buttons',
            exportSuffix: '40',
            order: 2,
            properties: {
                bg_color:     { type: 'color', format: 'hex8' },
                bg_opa:       { type: 'int',   min: 0, max: 255, internal: true },
                border_color: { type: 'color' },
                border_width: { type: 'int',   min: 0 },
                radius:       { type: 'int',   min: 0, max: 999 },
                text_color:   { type: 'color' },
                text_font:    { type: 'int',   min: 8 },
                text_font_family: { type: 'string', internal: true },
            },
        },
    },

    properties: {
        x:           { type: 'int', internal: true },
        y:           { type: 'int', internal: true },
        width:       { type: 'int', min: 0, internal: true },
        height:      { type: 'int', min: 0, internal: true },
        min_width:   { type: 'int', min: 0 },
        min_height:  { type: 'int', min: 0 },
        haspid:      { type: 'int', min: 1, max: 254, internal: true },
        parentid:    { type: 'int', min: 0, max: 254, internal: true },
        enabled:     { type: 'bool' },
        hidden:      { type: 'bool' },
        text:        { type: 'string' },
        options:    { type: 'array', internal: true },
        // `modal` removed: ATTR_MODAL is defined as a hash constant in firmware but never
        // referenced in the dispatch switch or msgbox creation code — sending `modal: true` is
        // silently ignored by real firmware. No editor-only usage found in HaspMsgbox.js either.
        auto_close: { type: 'int', min: 0, unit: 'ms', label: 'auto close' },
        // Font Family selection for the message body text (main part) — see
        // label.js's `font` property comment for the full wire-format
        // semantics.
        font: { type: 'resource', resourceType: 'font', label: 'Font Family', inline: true },
        // Font Family selection for the buttons' text (item_buttons part) —
        // independent slot from `font` above, since they're separate parts
        // (see gauge.js's `value_font_resource` for the naming convention used
        // for a widget's secondary font-resource slot).
        item_font_resource: { type: 'resource', resourceType: 'font', label: 'Item Font Family', inline: true },
    },

    defaults: {
        properties: {
            x: 0, y: 0, width: 240, height: 120, min_width: 0, min_height: 0,
            enabled: true, hidden: false,
            text: 'Message',
            options: ['OK'],
            auto_close: 0,
            font: '',
            item_font_resource: '',
        },
    },
    states: ['pressed'],

    editorCapabilities:  ['can_transform', 'no_geom'],
    runtimeCapabilities: ['has_action'],

    events: SELECTION_EVENTS,

    bindings: {
        properties: {},
        actions: {
            hass_services: ['', 'homeassistant', 'openhasp', 'light', 'switch', 'scene', 'script', 'automation', 'esphome'],
        },
    },

    automation: {
        runtimeVariables: [
            { name: 'val', type: 'int', label: 'Pressed Button (index)' },
        ],
        actionDomains: ['homeassistant', 'light', 'switch', 'scene', 'automation', 'openhasp', 'esphome'],
        // Legacy hass_config.events[] rows for this widget are one-per-button
        // rather than one-per-event-type — each row is discriminated at
        // migration time by comparing this runtime variable to the row's
        // 0-based position (see LegacyEventsToAst.js's migrateHassConfigToAst).
        legacyRowIndexVariable: 'val',
        // Legacy msgbox rows had no event picker and always meant `down`.
        // Keep that migration contract separate from the complete firmware
        // event set above.
        legacyTrigger: 'down',
    },
}

// Internal button schema — only text is user-editable; position/size driven by the msgbox
export const msgbox_btn = {
    parts: {},

    properties: {
        text: { type: 'string' },
    },

    defaults: { properties: { text: '' } },

    editorCapabilities:  ['no_haspid', 'no_comment', 'no_gridsnap', 'no_export', 'no_geom'],
    runtimeCapabilities: [],

    events: {},
    bindings: { properties: {}, actions: {}, events: {} },
}
